// AdminTfvfV1_2CalibrationPage.js — staging-only calibration console.
// Master-admin only. Shows the shadow-grade set for TFVF V1.2 with the
// journey-stage / experience / DISC / cap distributions, side-by-side
// with the V1.1 baseline so the pilot can validate before promoting.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { FlaskConical, Loader2, RefreshCw, ShieldAlert, CheckCircle2, Send, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, tone = 'default' }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
    <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
    <div className={`text-2xl font-black mt-1 ${tone === 'warn' ? 'text-amber-300' : tone === 'good' ? 'text-teal-300' : 'text-white'}`}>{value}</div>
  </div>
);

const Bucket = ({ title, dist }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-3">{title}</h3>
    <div className="space-y-1.5">
      {Object.entries(dist || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-sm">
          <span className="font-mono text-teal-300">{k}</span>
          <span className="text-white/80 font-semibold">{v}</span>
        </div>
      ))}
      {Object.keys(dist || {}).length === 0 && (
        <div className="text-white/40 text-xs italic">No entries yet.</div>
      )}
    </div>
  </div>
);

const AdminTfvfV1_2CalibrationPage = () => {
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Paste-and-grade panel state
  const [transcript, setTranscript] = useState('');
  const [contextJson, setContextJson] = useState(
    '{\n  "context_confidence": "HIGH",\n  "previous_call_count": 0,\n  "previous_sms_count": 0,\n  "previous_appointment_count": 0,\n  "pricing_sent": false,\n  "visit_status": null,\n  "club": "Chermside"\n}'
  );
  const [callTypeInput, setCallTypeInput] = useState('NEW SALES');
  const [humanScoreInput, setHumanScoreInput] = useState('');
  const [humanStageInput, setHumanStageInput] = useState('');
  const [grading, setGrading] = useState(false);
  const [gradeResult, setGradeResult] = useState(null);
  const [gradeError, setGradeError] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/admin/tfvf/v1_2/calibration/report`, { headers });
      setReport(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const runGrade = useCallback(async () => {
    setGradeError(''); setGradeResult(null);
    if (!transcript.trim() || transcript.trim().length < 40) {
      setGradeError('Transcript looks too short — paste at least a few exchanges.');
      return;
    }
    let ctx;
    try {
      ctx = JSON.parse(contextJson || '{}');
    } catch (e) {
      setGradeError(`Pre-call context JSON invalid: ${e.message}`);
      return;
    }
    setGrading(true);
    try {
      const r = await axios.post(`${API}/admin/tfvf/v1_2/grade-shadow`, {
        transcript, pre_call_context: ctx, call_type: callTypeInput,
      }, { headers });
      setGradeResult({
        ...r.data,
        _human_score: humanScoreInput ? Number(humanScoreInput) : null,
        _human_stage: humanStageInput || null,
      });
      await load();
    } catch (e) {
      setGradeError(e?.response?.data?.detail || 'Grade failed.');
    } finally {
      setGrading(false);
    }
  }, [transcript, contextJson, callTypeInput, humanScoreInput, humanStageInput, headers, load]);

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="tfvf-v1-2-calibration-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-teal-400" />
              TFVF V1.2 Shadow Calibration
              <span className="ml-2 text-xs bg-amber-500/15 text-amber-300 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">Staging</span>
            </h1>
            <p className="text-sm text-white/60 mt-1 max-w-3xl">
              Read-only view of the V1.2 shadow-grade set. V1.1 production grades are untouched.
              Promote V1.2 to production only after human calibration approval.
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10"
            data-testid="reload-report"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reload
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-200">
            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {report && (
          <>
            {/* Paste-and-grade panel — calibration session workhorse */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6" data-testid="v1-2-grade-panel">
              <h2 className="text-white font-bold text-lg mb-1">Shadow-grade a call</h2>
              <p className="text-white/60 text-xs mb-4">
                Paste a transcript, tweak the pre-call context, add your human grade for comparison, and click <strong className="text-white">Grade this call</strong>.
                The record is stored in <code className="bg-white/10 px-1.5 rounded text-[10px]">tfvf_v1_2_shadow_grades</code>. V1.1 grades stay untouched.
              </p>
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1.5">Transcript</label>
                  <textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    rows={12}
                    placeholder={"Agent: Hi from Total Fusion Chermside...\nCaller: Hey, I'm just wondering about your gym...\nAgent: What sparked your interest?\n..."}
                    className="w-full bg-[#0A0F1C] text-white/90 border border-white/10 rounded-lg p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="v1-2-transcript-input"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1.5">Call type</label>
                    <select
                      value={callTypeInput} onChange={(e) => setCallTypeInput(e.target.value)}
                      className="w-full bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      data-testid="v1-2-call-type"
                    >
                      <option>NEW SALES</option>
                      <option>FOLLOW-UP/REACTIVATION</option>
                      <option>MEMBER/ADMIN</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1.5">Pre-call context (JSON)</label>
                    <textarea
                      value={contextJson} onChange={(e) => setContextJson(e.target.value)}
                      rows={7}
                      className="w-full bg-[#0A0F1C] text-teal-200 border border-white/10 rounded-lg p-2 text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                      data-testid="v1-2-context-json"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-white/60 text-[10px] uppercase tracking-wider font-semibold mb-1">Human score</label>
                      <input
                        type="number" min="0" max="100" step="0.5" value={humanScoreInput}
                        onChange={(e) => setHumanScoreInput(e.target.value)}
                        placeholder="e.g. 72"
                        className="w-full bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="v1-2-human-score"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-[10px] uppercase tracking-wider font-semibold mb-1">Human stage</label>
                      <input
                        type="text" value={humanStageInput}
                        onChange={(e) => setHumanStageInput(e.target.value)}
                        placeholder="e.g. NEW_DISCOVERY"
                        className="w-full bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="v1-2-human-stage"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {gradeError && (
                <div className="mt-3 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-200" data-testid="v1-2-grade-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{gradeError}</span>
                </div>
              )}
              <div className="mt-4">
                <button
                  onClick={runGrade} disabled={grading}
                  className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-lg text-sm"
                  data-testid="v1-2-grade-submit"
                >
                  {grading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Grade this call
                </button>
              </div>

              {gradeResult && (
                <div className="mt-5 bg-teal-500/5 border border-teal-500/30 rounded-xl p-4" data-testid="v1-2-grade-result">
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-white/50 text-[10px] uppercase tracking-wider">V1.2 stage (before)</div>
                      <div className="text-teal-300 font-mono">{gradeResult.journey_stage_before_call}</div>
                      {gradeResult._human_stage && (
                        <div className="text-white/40 text-[10px] mt-0.5">
                          human: <span className={gradeResult._human_stage === gradeResult.journey_stage_before_call ? 'text-teal-300' : 'text-amber-300'}>{gradeResult._human_stage}</span>
                          {gradeResult._human_stage === gradeResult.journey_stage_before_call ? ' ✓ match' : ' ✗ differs'}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-white/50 text-[10px] uppercase tracking-wider">V1.2 score</div>
                      <div className="text-white font-bold text-xl">{gradeResult.overall_score}</div>
                      {gradeResult._human_score !== null && (
                        <div className="text-white/40 text-[10px] mt-0.5">
                          human: <span className="text-teal-300">{gradeResult._human_score}</span> · delta: <span className={Math.abs(gradeResult._human_score - gradeResult.overall_score) < 8 ? 'text-teal-300' : 'text-amber-300'}>{(gradeResult.overall_score - gradeResult._human_score).toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-white/50 text-[10px] uppercase tracking-wider">DISC · experience · cap</div>
                      <div className="text-white text-sm">
                        <span className="font-mono text-teal-300">{gradeResult.disc_primary}</span>
                        <span className="text-white/40 mx-1.5">·</span>
                        <span className="font-mono text-teal-300">{gradeResult.experience_segment}</span>
                        <span className="text-white/40 mx-1.5">·</span>
                        <span className="font-mono text-amber-300">{gradeResult.score_cap_applied || 'none'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/60">
                    <div>Legacy subtype: <span className="text-white font-mono">{gradeResult.legacy_subtype}</span> · After stage: <span className="text-white font-mono">{gradeResult.journey_stage_after_call}</span> · SMS: <span className="text-white font-mono">{gradeResult.suggested_sms_strategy}</span></div>
                    {gradeResult.suggested_sms && (
                      <div className="mt-2 text-white/70 italic">&ldquo;{gradeResult.suggested_sms}&rdquo;</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-6" data-testid="v1-2-summary-stats">
              <Stat label="Shadow grades" value={report.count || 0} />
              <Stat label="Mean score" value={report.score_summary?.mean ?? '—'} tone="good" />
              <Stat label="Score range" value={`${report.score_summary?.min ?? 0}–${report.score_summary?.max ?? 0}`} />
              <Stat label="Caps triggered" value={Object.values(report.cap_distribution || {}).reduce((s, x) => s + x, 0)} tone="warn" />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Bucket title="Journey stages (before)" dist={report.stage_distribution} />
              <Bucket title="Experience segments" dist={report.experience_distribution} />
              <Bucket title="DISC primaries" dist={report.disc_distribution} />
              <Bucket title="Score caps" dist={report.cap_distribution} />
            </div>

            <div className="mt-8 bg-teal-500/5 border border-teal-500/20 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-teal-400 shrink-0 mt-0.5" />
                <div className="text-sm text-white/80 leading-relaxed">
                  <p className="text-white font-semibold mb-1">Guardrails enforced by V1.2</p>
                  <ul className="list-disc pl-5 space-y-1 text-white/70">
                    <li>V1.1 production grades untouched — V1.2 lives in <code className="bg-white/10 px-1.5 rounded text-xs">tfvf_v1_2_shadow_grades</code></li>
                    <li>DISC inferred from observed caller behaviour only — never from journey stage or experience</li>
                    <li>Grading uses BEFORE-call stage — after-call stage is metadata only</li>
                    <li>Legacy 4 subtypes preserved on every record</li>
                    <li>SMS is recommended but never auto-sent</li>
                    <li>Score caps 29 / 49 / 59 / 69 preserved from V1.1</li>
                    <li>Talk-share surfaced as coaching signal — never caps score</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTfvfV1_2CalibrationPage;
